import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { apiFetch } from '../config/api';

export type DAppSessionStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'terminated';

export interface DAppSession {
  sessionId: string;
  dappName: string;
  dappUrl: string;
  walletAddress?: string;
  status: DAppSessionStatus;
  pairingUri?: string;
  requestedChains: string[];
  requestedPermissions: string[];
  walletConnect: boolean;
  expiresAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

interface WalletContextType {
  address: string | null;          // HLF 地址 (用於 duel 後端)
  ethAddress: string | null;       // 以太坊地址 (從 GiGiWallet 獲取)
  chainId: string | null;
  balance: string | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  connectWallet: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  sessionInfo: DAppSession | null;
  isSessionModalOpen: boolean;
  closeSessionModal: () => void;
  cancelSession: () => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'duel_dapp_session';
const WALLET_STORAGE_KEY = 'duel_dapp_wallet';

const saveSessionToStorage = (session: DAppSession | null) => {
  if (typeof window === 'undefined') return;
  if (!session) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

const loadSessionFromStorage = (): DAppSession | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.sessionId) {
      return parsed as DAppSession;
    }
  } catch (error) {
    console.error('[WalletContext] ❌ Failed to parse stored session', error);
  }
  return null;
};

// 保存 wallet 資料到 localStorage
const saveWalletToStorage = (walletData: { address: string; ethAddress: string; chainId: string; balance: string } | null) => {
  if (typeof window === 'undefined') return;
  if (!walletData) {
    window.localStorage.removeItem(WALLET_STORAGE_KEY);
    console.log('[WalletContext] 🗑️ Cleared wallet from localStorage');
    return;
  }
  window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(walletData));
  console.log('[WalletContext] 💾 Saved wallet to localStorage:', walletData.address);
};

// 從 localStorage 恢復 wallet 資料
const loadWalletFromStorage = (): { address: string; ethAddress: string; chainId: string; balance: string } | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(WALLET_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.address) {
      console.log('[WalletContext] 📂 Loaded wallet from localStorage:', parsed.address);
      return parsed;
    }
  } catch (error) {
    console.error('[WalletContext] ❌ Failed to parse stored wallet', error);
  }
  return null;
};

export function WalletProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<string | null>(null);          // HLF 地址
  const [ethAddress, setEthAddress] = useState<string | null>(null);    // ETH 地址
  const [chainId, setChainId] = useState<string | null>(null);
  const [balance, setBalance] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionInfo, setSessionInfo] = useState<DAppSession | null>(null);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const sessionPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkWallet = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // 1. 檢查是否在 GiGiWallet WebView 中
      let walletEthAddress: string | null = null;
      
      // 優先使用 window.ethereum (Web3 Provider)
      if (typeof window !== 'undefined' && (window as any).ethereum) {
        console.log('[WalletContext] 🔍 Detected window.ethereum');
        try {
          // 檢查是否已經有 _selectedAddress (直接注入的)
          if ((window as any).ethereum._selectedAddress) {
            walletEthAddress = (window as any).ethereum._selectedAddress;
            console.log('[WalletContext] ✅ Got address from ethereum._selectedAddress:', walletEthAddress);
          } else {
            // Attempt to request accounts
            const accounts = await (window as any).ethereum.request({ 
              method: 'eth_requestAccounts' 
            });
            if (accounts && accounts.length > 0) {
              walletEthAddress = accounts[0];
              console.log('[WalletContext] ✅ Got ETH address from window.ethereum:', walletEthAddress);
            }
          }
        } catch (ethError) {
          console.error('[WalletContext] ❌ Error getting accounts from ethereum:', ethError);
        }
      }
      
      // 回退到 GiGiWallet
      if (!walletEthAddress && typeof window !== 'undefined' && (window as any).GiGiWallet) {
        const wallet = (window as any).GiGiWallet;
        walletEthAddress = wallet.address;
        console.log('[WalletContext] ✅ Got address from GiGiWallet:', walletEthAddress);
      }

      if (!walletEthAddress) {
        console.log('[WalletContext] ⚠️ No wallet detected');
        return false;
      }

      // 2. 在 GiGi Wallet 中,ETH 地址和 HLF 地址是同一個
      // 直接使用 ETH 地址作為 HLF 地址
      console.log('[WalletContext] 🔄 Using ETH address as HLF address:', walletEthAddress);
      
      // 3. 設置地址
      setAddress(walletEthAddress);        // 使用 ETH 地址作為 HLF 地址
      setEthAddress(walletEthAddress);      // ETH 地址保存
      setChainId('HLF');
      setIsConnected(true);
      
      console.log('[WalletContext] ✅ Connected successfully!');
      console.log('[WalletContext]    HLF Address:', walletEthAddress);
      console.log('[WalletContext]    ETH Address:', walletEthAddress);
      
      // 4. 獲取餘額
      await fetchBalance(walletEthAddress);
      
      // 5. ✅ 保存到 localStorage
      saveWalletToStorage({
        address: walletEthAddress,
        ethAddress: walletEthAddress,
        chainId: 'HLF',
        balance: '0' // 會在 fetchBalance 後更新
      });
      
      return true;
    } catch (err: any) {
      console.error('[WalletContext] ❌ Error checking wallet:', err);
      setError(err.message || 'Error connecting wallet');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalance = async (walletAddress: string) => {
    try {
      const url = `/wallet/${walletAddress}/balance`;
      
      console.log(`[WalletContext] 🔍 Fetching balance from: ${url}`);
      console.log(`[WalletContext] 🌐 Using wallet address: ${walletAddress}`);
      
      const response = await apiFetch(url);
      console.log(`[WalletContext] 📡 Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[WalletContext] 📦 Balance API response:', JSON.stringify(data, null, 2));
        
        if (data.success && data.data) {
          const availableBalance = data.data.available || data.data.balance || '0';
          setBalance(availableBalance);
          console.log(`[WalletContext] ✅ Balance loaded: ${availableBalance} CHNX`);
          
          // 更新 localStorage 中的餘額
          if (isConnected && address) {
            saveWalletToStorage({
              address,
              ethAddress: ethAddress || address,
              chainId: chainId || 'HLF',
              balance: availableBalance
            });
          }
        } else {
          console.warn('[WalletContext] ⚠️ Invalid response format:', data);
          setBalance('0');
        }
      } else {
        const errorText = await response.text();
        console.error('[WalletContext] ❌ HTTP Error:', response.status, errorText);
        setBalance('0');
      }
    } catch (error) {
      console.error('[WalletContext] ❌ Error fetching balance:', error);
      console.error('[WalletContext] ❌ Error details:', error instanceof Error ? error.message : String(error));
      setBalance('0');
    }
  };

  const refreshBalance = async () => {
    if (address) {
      await fetchBalance(address);
    }
  };

  const clearSessionPolling = () => {
    if (sessionPollingRef.current) {
      clearInterval(sessionPollingRef.current);
      sessionPollingRef.current = null;
    }
  };

  const closeSessionModal = () => {
    setIsSessionModalOpen(false);
  };

  const applyApprovedSession = (session: DAppSession) => {
    if (session.walletAddress) {
      setAddress(session.walletAddress);
      setChainId('HLF');
      setIsConnected(true);
      fetchBalance(session.walletAddress).catch((balanceError) => {
        console.error('[WalletContext] ❌ Failed to refresh balance after approval:', balanceError);
      });
    }
    if (session.metadata?.ethAddress) {
      setEthAddress(session.metadata.ethAddress);
    }
    setError(null);
    clearSessionPolling();
    setSessionInfo(session);
    saveSessionToStorage(null);
    setIsSessionModalOpen(false);
  };

  const handleSessionStatus = (session: DAppSession) => {
    setSessionInfo(session);
    if (session.status === 'pending') {
      saveSessionToStorage(session);
    } else {
      saveSessionToStorage(null);
    }
    if (session.status === 'approved') {
      applyApprovedSession(session);
    } else if (session.status === 'rejected') {
      setError('Wallet rejected the connection request');
      clearSessionPolling();
    } else if (session.status === 'expired') {
      setError('Session expired, please try connecting again');
      clearSessionPolling();
    } else if (session.status === 'terminated') {
      clearSessionPolling();
    }
  };

  const startSessionPolling = (sessionId: string) => {
    clearSessionPolling();
    sessionPollingRef.current = setInterval(async () => {
      try {
        const response = await apiFetch(`/dapp/sessions/${sessionId}`);
        if (!response.ok) {
          console.error('[WalletContext] ❌ Session polling failed:', response.status);
          return;
        }
        const data = await response.json();
        if (data.success && data.data) {
          handleSessionStatus(data.data as DAppSession);
        }
      } catch (pollError) {
        console.error('[WalletContext] ❌ Error polling session status:', pollError);
      }
    }, 4000);
  };

  // Session handshake is not needed in WebView environment
  // Backend does not have /api/dapp/sessions endpoint
  /*
  const initiateSession = async () => {
    try {
      const metadataPayload = {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dapp: 'duel',
      };

      const payload = {
        dappName: 'Duel DApp',
        dappUrl: window?.location?.origin || 'https://devduel.one-chain.io',
        requestedChains: ['hlf'],
        requestedPermissions: ['signTransaction', 'viewAddress'],
        walletConnect: true,
        metadata: metadataPayload,
      };

      const response = await apiFetch('/dapp/sessions', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[WalletContext] ❌ Failed to create session:', response.status, errorText);
        // 靜默失敗 - 在 GiGiWallet WebView 中不需要 session API
        console.log('[WalletContext] ⚠️ Session API not available, wallet may need direct connection');
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        const session: DAppSession = data.data;
        setSessionInfo(session);
        setIsSessionModalOpen(true);
        saveSessionToStorage(session);
        startSessionPolling(session.sessionId);
      } else {
        console.warn('[WalletContext] ⚠️ Unexpected session response from server');
      }
    } catch (sessionError: any) {
      console.error('[WalletContext] ❌ Error creating session:', sessionError);
      // 靜默失敗 - 在 GiGiWallet WebView 中不需要 session API
      console.log('[WalletContext] ⚠️ Session creation failed, wallet may already be connected');
    }
  };
  */

  const cancelSession = async () => {
    if (!sessionInfo) {
      setIsSessionModalOpen(false);
      return;
    }
    try {
      await apiFetch(`/dapp/sessions/${sessionInfo.sessionId}`, {
        method: 'DELETE',
      });
    } catch (cancelError) {
      console.error('[WalletContext] ❌ Failed to cancel session:', cancelError);
    } finally {
      clearSessionPolling();
      setSessionInfo(null);
      saveSessionToStorage(null);
      setIsSessionModalOpen(false);
    }
  };

  const connectWallet = async () => {
    console.log('[WalletContext] 🔄 Manual connection requested');
    console.log('[WalletContext] 🔍 Current state:', {
      isConnected,
      address,
      isLoading,
      error
    });
    
    // Debug: Check what wallet objects are available
    console.log('[WalletContext] 🔍 Checking wallet providers:');
    console.log('[WalletContext]    window:', Object.keys(window).filter(k => k.includes('ethereum') || k.includes('GiGi') || k.includes('wallet')));
    console.log('[WalletContext]    window.ethereum:', typeof (window as any).ethereum);
    console.log('[WalletContext]    window.GiGiWallet:', typeof (window as any).GiGiWallet);
    
    if ((window as any).ethereum) {
      console.log('[WalletContext]    ethereum._selectedAddress:', (window as any).ethereum._selectedAddress);
      console.log('[WalletContext]    ethereum.selectedAddress:', (window as any).ethereum.selectedAddress);
      console.log('[WalletContext]    ethereum._isConnected:', (window as any).ethereum._isConnected);
      console.log('[WalletContext]    ethereum.isConnected:', (window as any).ethereum.isConnected);
      console.log('[WalletContext]    ethereum.chainId:', (window as any).ethereum.chainId);
      console.log('[WalletContext]    ethereum.isGiGiWallet:', (window as any).ethereum.isGiGiWallet);
    }
    
    if ((window as any).GiGiWallet) {
      console.log('[WalletContext]    GiGiWallet.address:', (window as any).GiGiWallet.address);
      console.log('[WalletContext]    GiGiWallet.isConnected:', (window as any).GiGiWallet.isConnected);
      console.log('[WalletContext]    GiGiWallet object:', (window as any).GiGiWallet);
    }
    
    // 檢查 AndroidWeb3Bridge
    if (typeof (window as any).AndroidWeb3Bridge !== 'undefined') {
      console.log('[WalletContext]    ✅ AndroidWeb3Bridge is available');
    } else {
      console.log('[WalletContext]    ❌ AndroidWeb3Bridge NOT available');
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const success = await checkWallet();
      console.log('[WalletContext] 📊 checkWallet result:', success);
      
      if (!success) {
        // 在 WebView 環境中，如果 checkWallet 失敗，說明注入尚未完成
        // 不要嘗試創建 session (backend 沒有這個 API)
        console.log('[WalletContext] ⚠️ Wallet not detected yet');
        console.log('[WalletContext] 💡 Please wait for wallet injection or open in GiGi Wallet app');
        setError('Please open this DApp in GiGi Wallet app');
        setIsConnected(false);
      }
    } catch (err: any) {
      console.error('[WalletContext] ❌ Error in connectWallet:', err);
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      clearSessionPolling();
    };
  }, []);

  useEffect(() => {
    const storedSession = loadSessionFromStorage();
    if (!storedSession) {
      return;
    }
    setSessionInfo(storedSession);
    if (storedSession.status === 'approved') {
      applyApprovedSession(storedSession);
    } else if (storedSession.status === 'pending') {
      setIsSessionModalOpen(true);
      startSessionPolling(storedSession.sessionId);
    } else {
      // For rejected/expired/terminated we simply keep the info for UX
      setIsSessionModalOpen(true);
    }
  }, []);

  useEffect(() => {
    // Auto-connect on page load
    console.log('[WalletContext] 🚀 Initializing wallet connection...');
    console.log('[WalletContext] 🔍 Checking localStorage...');
    
    // 1. 先從 localStorage 恢復錢包狀態
    const cachedWallet = loadWalletFromStorage();
    console.log('[WalletContext] 📂 localStorage result:', cachedWallet);
    
    if (cachedWallet) {
      console.log('[WalletContext] 📦 Restored wallet from localStorage:', cachedWallet);
      setAddress(cachedWallet.address);
      setEthAddress(cachedWallet.ethAddress);
      setChainId(cachedWallet.chainId);
      setBalance(cachedWallet.balance);
      setIsConnected(true);
      
      // 立即嘗試更新餘額（使用 cached 地址）
      console.log('[WalletContext] 💰 Fetching latest balance for cached wallet...');
      fetchBalance(cachedWallet.address).catch(err => {
        console.warn('[WalletContext] ⚠️ Failed to fetch balance on init:', err);
      });
    } else {
      console.log('[WalletContext] ⚠️ No cached wallet found in localStorage');
    }
    
    // Wait for window.ethereum to be ready
    const initWallet = async () => {
      // Wait a bit for provider to be injected
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Try to connect
      if (typeof window !== 'undefined' && (window as any).ethereum) {
        console.log('[WalletContext] ✅ window.ethereum detected, auto-connecting...');
        checkWallet();
      } else if (typeof window !== 'undefined' && (window as any).GiGiWallet) {
        console.log('[WalletContext] ✅ window.GiGiWallet detected, auto-connecting...');
        checkWallet();
      } else {
        console.log('[WalletContext] ⚠️ No wallet provider detected, will wait for injection...');
        // 設置一個更長的延遲重試
        setTimeout(() => {
          console.log('[WalletContext] 🔄 Retrying wallet detection after 2s...');
          if (typeof window !== 'undefined' && ((window as any).ethereum || (window as any).GiGiWallet)) {
            console.log('[WalletContext] ✅ Wallet provider now available!');
            checkWallet();
          }
        }, 2000);
      }
    };
    
    initWallet();
    
    // Listen for wallet connected events
    const handleWalletConnected = () => {
      console.log('[WalletContext] 🎉 Wallet connected event received');
      checkWallet();
    };
    
    const handleAccountsChanged = (accounts: string[]) => {
      console.log('[WalletContext] 🔄 Accounts changed event received:', accounts);
      checkWallet();
    };

    window.addEventListener('wallet-connected', handleWalletConnected);
    window.addEventListener('ethereum#initialized', handleWalletConnected);
    
    // 監聽 ethereum 的 accountsChanged 事件
    if (typeof window !== 'undefined' && (window as any).ethereum) {
      (window as any).ethereum.on?.('accountsChanged', handleAccountsChanged);
      (window as any).ethereum.on?.('connect', handleWalletConnected);
    }

    return () => {
      window.removeEventListener('wallet-connected', handleWalletConnected);
      window.removeEventListener('ethereum#initialized', handleWalletConnected);
      
      if (typeof window !== 'undefined' && (window as any).ethereum) {
        (window as any).ethereum.removeListener?.('accountsChanged', handleAccountsChanged);
        (window as any).ethereum.removeListener?.('connect', handleWalletConnected);
      }
    };
  }, []);

  return (
    <WalletContext.Provider
      value={{
        address,
        ethAddress,
        chainId,
        balance,
        isConnected,
        isLoading,
        error,
        connectWallet,
        refreshBalance,
        sessionInfo,
        isSessionModalOpen,
        closeSessionModal,
        cancelSession,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}

